<template>
  <Flyout
    ref="flyout"
    class="flyout-container"
    :class="containerClass"
    v-bind="componentProps"
  >
    <template #default>
      <div v-html="flyoutState.content" />
      <template v-if="flyoutState.component">
        <component
          :is="flyoutState.component.is"
          v-bind="flyoutState.component.props"
          :close="closeFlyout"
        />
      </template>
    </template>
    <template #footer>
      <template
        v-for="(button, key) in flyoutState.buttons"
        :key="key"
      >
        <LinkButton
          v-bind="getFilteredProps(button)"
          @click="() => click(button)"
        >
          {{ button.name }}
        </LinkButton>
      </template>
    </template>
  </Flyout>
</template>

<script setup lang="ts">
import { useRoute } from '#imports';
import { Flyout, LinkButton, type ModalButton } from '@i22team/td-longtail-vue-components';
import { flyoutState, flyout, closeFlyout } from '~/composables/flyout';

const props = defineProps({
  withNav: {
    type: Boolean,
  },
});

const componentProps = computed(() => ({
  success: flyoutState.success,
  warning: flyoutState.warning,
  error: flyoutState.error,
  closeOnBackdrop: flyoutState.closeOnBackdrop,
  headingText: flyoutState.heading,
  ariaLabel: flyoutState.ariaLabel,
}));

const getFilteredProps = (btnProps: ModalButton) => {
  const { click, name, ...passThroughProps }: Partial<ModalButton> = btnProps;
  if (!passThroughProps.tag) {
    passThroughProps.tag = 'button';
    passThroughProps.type = 'button';
  }
  return passThroughProps;
};

const click = async (button: ModalButton) => {
  if (typeof button.click === 'function') {
    await button.click(closeFlyout, flyout);
  } else {
    closeFlyout();
  }
};

const route = useRoute();

const unwatch = watch(
  route,
  () => {
    closeFlyout();
  },
  { deep: true }
);

onUnmounted(() => {
  unwatch();
});

const containerClass = computed(() => (props.withNav ? 'flyout-container--with-nav' : ''));
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.flyout-container {
  --top-mobile: 0px;
  --top-desktop: 0px;
  border-bottom: 1px solid var(--color-stroke);

  &--with-nav {
    --top-mobile: var(--nav-height-initial);
    --top-desktop: var(--nav-height-initial);
  }
}
</style>
